import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { AppMode, ImageStyle, GeneratedImage } from '@/types';
import { useDataLoader } from '@/hooks/useDataLoader';
import { Location, Template, PresetData } from '@/api/apiClient';
import { DIRECTUS_INSTANCE } from '@/config/env';
import { getSecureToken, setSecureToken, removeSecureToken } from '@/utils/tokenEncryption';
import { pwaChannel } from '@/utils/pwaChannel';

interface AppContextType {
  // Auth
  isAuthenticated: boolean;
  isGuestMode: boolean; // anonymous but allowed to generate (local-only)
  setIsAuthenticated: (value: boolean) => void;
  enterGuestMode: () => void;
  exitGuestMode: () => void;
  signOut: () => void;
  user: DirectusMe | null;
  userLoaded: boolean;
  loadMe: () => Promise<void>;
  refreshToken: () => Promise<boolean>;

  // Mode
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  
  // Active Module
  activeModule: ImageStyle;
  setActiveModule: (module: ImageStyle) => void;
  
  // Theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;
  
  // Gallery
  images: GeneratedImage[];
  addImage: (image: GeneratedImage) => void;
  setAllImages: (images: GeneratedImage[]) => void;
  appendImages: (images: GeneratedImage[]) => void;
  currentImageIndex: number;
  setCurrentImageIndex: (index: number) => void;
  galleryViewMode: 'single' | 'grid';
  setGalleryViewMode: (mode: 'single' | 'grid') => void;
  
  // Data (locations, templates, presets)
  locations: Location[] | null;
  templates: Template[] | null;
  presets: PresetData | null;
  dataLoading: boolean;
  
  // Generation state
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
}

export interface DirectusMe {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  onboarding_completed?: boolean;
  trial_ends?: string | null;
  subscription_status?: string | null;
  subscription_plan?: string | null;
  avatar?: string | null;
  linked_workspace?: {
    id: string;
    workspace_name?: string;
    credits?: number;
  } | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AppMode>('create');
  const [activeModule, setActiveModule] = useState<ImageStyle>('packshot');
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [galleryViewMode, setGalleryViewMode] = useState<'single' | 'grid'>(() => {
    const saved = localStorage.getItem('galleryViewMode');
    return (saved === 'grid' || saved === 'single') ? saved : 'single';
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getSecureToken('auth_token')));
  const [isGuestMode, setIsGuestMode] = useState(() => {
    const token = getSecureToken('auth_token');
    const guest = localStorage.getItem('guest_mode') === 'true';
    return !token && guest;
  });
  const [user, setUser] = useState<DirectusMe | null>(null);
  const [userLoaded, setUserLoaded] = useState(() => !Boolean(getSecureToken('auth_token')));

  // Load data (locations, templates, presets)
  const { locations, templates, presets, loading: dataLoading, reload: reloadData } = useDataLoader();

  const refreshToken = async (): Promise<boolean> => {
    try {
      const token = getSecureToken('auth_token');
      const refreshTokenValue = getSecureToken('refresh_token');
      
      if (!token || !refreshTokenValue) {
        return false;
      }

      const response = await fetch(`${DIRECTUS_INSTANCE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue
        })
      });

      const data = await response.json();

      if (!response.ok || !data.data?.access_token) {
        return false;
      }

      // Update tokens
      setSecureToken('auth_token', data.data.access_token);
      if (data.data.refresh_token) {
        setSecureToken('refresh_token', data.data.refresh_token);
      }

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const loadMe = async () => {
    setUserLoaded(false);
    const token = getSecureToken('auth_token');
    if (!token) {
      setUser(null);
      setUserLoaded(true);
      return;
    }
    try {
      const meFields = 'id,email,first_name,last_name,onboarding_completed,trial_ends,subscription_status,subscription_plan,avatar,linked_workspace.id,linked_workspace.workspace_name,linked_workspace.credits';
      const res = await fetch(`${DIRECTUS_INSTANCE}/users/me?fields=${encodeURIComponent(meFields)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If 401, try to refresh token and retry
      if (res.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry with new token
          const newToken = getSecureToken('auth_token');
          const retryRes = await fetch(`${DIRECTUS_INSTANCE}/users/me?fields=${encodeURIComponent(meFields)}`, {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          const retryJson = await retryRes.json();
          if (retryRes.ok) {
            setUser(retryJson?.data || null);
            setUserLoaded(true);
            return;
          }
        }
        // If refresh failed, sign out
        signOut();
        setUserLoaded(true);
        return;
      }
      
      const json = await res.json();
      if (!res.ok) {
        setUser(null);
        setUserLoaded(true);
        return;
      }
      setUser(json?.data || null);
      setUserLoaded(true);
      
      // Keep templates stable; avoid extra reload pressure on every loadMe call.
    } catch {
      setUser(null);
      setUserLoaded(true);
    }
  };

  // Listen for auth:logout event from fetchWithAuth
  useEffect(() => {
    const handleAuthLogout = () => {
      signOut();
    };
    
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  // Auto-load user data on mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      loadMe();
    } else {
      setUser(null);
      setUserLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    // Update document class for Tailwind dark mode
    document.documentElement.classList.toggle('dark');
  };

  const enterGuestMode = () => {
    setIsAuthenticated(false);
    setIsGuestMode(true);
    localStorage.setItem('guest_mode', 'true');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  const exitGuestMode = () => {
    setIsGuestMode(false);
    localStorage.removeItem('guest_mode');
  };

  const signOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('guest_mode');
    setIsAuthenticated(false);
    setIsGuestMode(false);
    setUser(null);
    setUserLoaded(true);
  };

  const addImage = useCallback((image: GeneratedImage) => {
    setImages(prev => [image, ...prev]);
    setCurrentImageIndex(0);
    setMode('browse'); // Switch to browse mode after generation
    // Notify other open tabs / PWA instances that gallery data changed.
    pwaChannel?.postMessage({ type: 'GALLERY_UPDATED' });
  }, [setMode]);

  const setAllImages = useCallback((newImages: GeneratedImage[]) => {
    setImages(newImages);
    if (newImages.length > 0) {
      setCurrentImageIndex(0);
    }
  }, []);

  const appendImages = useCallback((newImages: GeneratedImage[]) => {
    setImages(prev => [...prev, ...newImages]);
  }, []);

  // Sync galleryViewMode to localStorage
  const handleSetGalleryViewMode = (mode: 'single' | 'grid') => {
    setGalleryViewMode(mode);
    localStorage.setItem('galleryViewMode', mode);
  };

  const value: AppContextType = {
    isAuthenticated,
    isGuestMode,
    setIsAuthenticated,
    enterGuestMode,
    exitGuestMode,
    signOut,
    user,
    userLoaded,
    loadMe,
    refreshToken,
    mode,
    setMode,
    activeModule,
    setActiveModule,
    theme,
    toggleTheme,
    images,
    addImage,
    setAllImages,
    appendImages,
    currentImageIndex,
    setCurrentImageIndex,
    galleryViewMode,
    setGalleryViewMode: handleSetGalleryViewMode,
    locations,
    templates,
    presets,
    dataLoading,
    isGenerating,
    setIsGenerating
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
